import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import moment from 'moment';
import { parseISO } from 'date-fns';

import { ModalContainer } from '../common/ModalContainer';
import { DatePickerFigo } from '../common';
import { ActionButton } from '../common/petCloud/ActionButton';

import { PET_DEMOS_SPECIES } from '../../constants';
import dogBlueAvatar from '../../assets/welcome/dog-blue-avatar.svg';
import catYellowAvatar from '../../assets/welcome/cat-avatar-yellow.svg';
import { usePetBreedsDemographics, useResponsive } from '../../hooks';
import { validateNumbersOnly } from '../../services/utils';

import styles from './AddPetDetailsModal.module.css';

const PET_NAME_MAX_LENGTH = 50;

const AddPetDetailsModal = ({
  isOpen = false,
  onClose = () => { },
  onSubmit = () => { },
  age = '',
  breed = '',
  breedId,
  coverageEffectiveDate = '',
  dateOfBirth = '',
  petName = '',
  specie = PET_DEMOS_SPECIES.dog,
  weight = '',
}) => {
  const [formState, setFormState] = useState(() => ({
    age,
    breed,
    breedId,
    coverageEffectiveDate,
    dateOfBirth,
    petName,
    specie,
    weight,
  }));
  const { isMobile } = useResponsive();

  const { breedDataDemographics } = usePetBreedsDemographics();

  const anyFieldEmpty = useMemo(() => (
    !formState.petName?.trim() || !formState.dateOfBirth || !formState.breed
    || !formState.weight || Number(formState.weight) <= 0
  ), [formState]);

  const maximumDateOfBirth = useMemo(() => {
    if (!formState.coverageEffectiveDate) {
      return null;
    }

    // maximum pet dateOfBirth is 56 days before coverage effective date
    return moment(formState.coverageEffectiveDate, 'MM/DD/YYYY')
      .subtract(56, 'days').toDate();
  }, [formState.coverageEffectiveDate]);

  // minimum date calculated from maximum date instead of from today... :fly:
  const minimumDateOfBirth = useMemo(() => {
    if (specie === PET_DEMOS_SPECIES.cat) {
      return moment(maximumDateOfBirth).subtract(25, 'years').toDate();
    }

    return moment(maximumDateOfBirth).subtract(20, 'years').toDate();
  }, [specie, maximumDateOfBirth]);

  const invalidDob = useMemo(() => {
    if (!formState.dateOfBirth || !maximumDateOfBirth) {
      return true;
    }

    const petDob = new Date(formState.dateOfBirth);

    return petDob > maximumDateOfBirth || petDob < minimumDateOfBirth;
  }, [formState.dateOfBirth, maximumDateOfBirth, minimumDateOfBirth]);

  const breedListWithFormat = useMemo(() => {
    if (!breedDataDemographics?.catBreeds?.length
      || !breedDataDemographics?.dogBreeds?.length) {
      return [];
    }

    const { catBreeds, dogBreeds } = breedDataDemographics;

    return specie === PET_DEMOS_SPECIES.cat
      ? catBreeds.map((catBreed) => ({
        label: catBreed.name,
        value: catBreed.id,
      })) : dogBreeds.map((dogBreed) => ({
        label: dogBreed.name,
        value: dogBreed.id,
      }));
  }, [breedDataDemographics, specie]);

  const { t } = useTranslation('petDemos');

  const specieLabel = specie === PET_DEMOS_SPECIES.dog ? t('dog') : t('cat');
  const specieIcon = specie === PET_DEMOS_SPECIES.dog
    ? dogBlueAvatar : catYellowAvatar;

  const updateForm = (partial) => {
    setFormState((prev) => ({ ...prev, ...partial }));
  };

  const handleChange = (value) => {
    // onBreedChange
    if (value.breed && value.breedId) {
      setFormState((prev) => ({
        ...prev,
        breed: value.breed,
        breedId: value.breedId,
      }));
    }

    // onDOBChanged ?
    if (value.dateOfBirth) {
      const ageInYears = moment().diff(value.dateOfBirth, 'years');

      setFormState((prev) => ({
        ...prev,
        age: ageInYears,
        dateOfBirth: value.dateOfBirth,
      }));
    }

    // onWeightChanged ?
    if (value.weight) {
      const { weight: newWeight } = value;
      if (validateNumbersOnly(newWeight) && newWeight > 0) {
        updateForm(value);
      }

      return;
    }

    updateForm(value);
  };

  const handleOnSubmit = () => {
    onSubmit(formState);
    onClose();
  };

  // Helper function to render common inputs
  const renderInput = (key, inputConfig) => {
    const {
      label,
      maxLength,
      name,
      onChange,
      type,
      value,
      options,
    } = inputConfig;

    if (type === 'date') {
      return (
        <div className={styles.inputContainer}>
          <span className={styles.label}>{label}</span>

          <div className={styles.datePickerContainer}>
            <DatePickerFigo
              autoFormat
              maxDate={maximumDateOfBirth}
              minDate={minimumDateOfBirth}
              name="pet-date-of-birth"
              onDateChange={onChange}
              selectedDate={value ? parseISO(value) : null}
            />
          </div>
        </div>
      );
    }

    if (type === 'select') {
      return (
        <div className={styles.inputContainer}>
          <span className={styles.label}>{label}</span>

          <Select
            menuPlacement={isMobile ? 'top' : 'bottom'}
            onChange={onChange}
            options={options}
            styles={{
              container: (provided) => ({
                ...provided,
                fontFamily: 'gotham-book',
                fontSize: '16px',
                padding: '0px',
                width: '100%',
              }),
              control: (provided) => ({
                ...provided,
                borderColor: 'var(--pc-color-onyx-200)',
                borderRadius: '8px',
                minHeight: 'auto',
                padding: '3px',
              }),
              dropdownIndicator: (provided, state) => ({
                ...provided,
                padding: '0px',
                // theres no way to know if the menu is open or not
                // so we use isFocused instead
                transform: state.isFocused ? 'rotate(180deg)' : '',
                transition: 'all .2s ease',
              }),
              indicatorsContainer: (provided) => ({
                ...provided,
                padding: '0px',
              }),
              indicatorSeparator: (provided) => ({
                ...provided,
                backgroundColor: 'transparent',
              }),
              input: (provided) => ({
                ...provided,
                margin: '0px',
                padding: '0px',
              }),
              menuList: (provided) => ({
                ...provided,
                '::-webkit-scrollbar': {
                  width: '12px',
                },
                '::-webkit-scrollbar-thumb': {
                  background: 'var(--pc-color-cotton-500)',
                  borderRadius: '8px',
                },
                '::-webkit-scrollbar-track': {
                  background: 'var(--pc-color-cotton-200)',
                },
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isFocused
                  ? 'var(--pc-color-blue-100)'
                  : 'var(--white)',
                color: 'var(--pc-color-onyx-300)',
              }),
              placeholder: (provided) => ({
                ...provided,
                marginTop: '2px', // because of font family
              }),
              singleValue: (provided) => ({
                ...provided,
                marginTop: '2px', // because of font family
              }),
              valueContainer: (provided) => ({
                ...provided,
                padding: '0px',
              }),
            }}
            value={{ label: value, value }}
          />
        </div>
      );
    }

    return (
      <label className={styles.inputContainer} htmlFor={name}>
        <span className={styles.label}>{label}</span>

        <input
          className={key === 'age' ? styles.inputDisabled : styles.input}
          disabled={key === 'age'}
          id={name}
          maxLength={maxLength}
          name={name}
          onChange={onChange}
          type={type}
          value={value}
        />
      </label>
    );
  };

  const inputData = {
    age: {
      label: t('addPet.ageAtEffective'),
      name: 'ageInput',
      onChange: null,
      type: 'number',
      value: formState.age,
    },
    breed: {
      label: t('addPet.breed'),
      name: 'breedInput',
      onChange: (newBreed) => handleChange({
        breed: newBreed.label,
        breedId: newBreed.value,
      }),
      options: breedListWithFormat,
      type: 'select',
      value: formState.breed || '',
    },
    dateOfBirth: {
      label: t('addPet.dateOfBirth'),
      name: 'dobInput',
      onChange: (date) => handleChange({ dateOfBirth: date }),
      type: 'date',
      value: formState.dateOfBirth || '',
    },
    petName: {
      label: t('addPet.petName'),
      maxLength: PET_NAME_MAX_LENGTH,
      name: 'petNameInput',
      onChange: (e) => handleChange({ petName: e.target.value }),
      type: 'text',
      value: formState.petName || '',
    },
    weight: {
      label: t('addPet.weight'),
      name: 'weightInput',
      onChange: (e) => handleChange({ weight: e.target.value }),
      value: formState.weight || '',
    },
  };

  return (
    <ModalContainer
      cancelable
      contentStyle={styles.modal}
      handleOutClick={onClose}
      id="petDemographicsDetails"
      show={isOpen}
    >
      <div className={styles.container}>
        <h3 className={styles.headerTitle}>{t('addPet.title')}</h3>

        <div className={styles.card}>
          <div className={styles.cardItem}>
            <h6 className={styles.cardLabel}>
              {t('addPet.specie')}
            </h6>

            <span className={styles.cardValue}>
              <img alt={specieLabel} src={specieIcon} />

              {specieLabel}
            </span>
          </div>

          <div className={`${styles.cardItem} ${styles.flex2Mobile}`}>
            <h6 className={styles.cardLabel}>
              {t('addPet.coverage')}
            </h6>

            <span className={styles.cardValue}>
              {formState.coverageEffectiveDate}
            </span>
          </div>
        </div>

        <span className={styles.bodyTitle}>{t('addPet.subtitle')}</span>

        <div className={styles.form}>
          <div className={styles.row}>
            {renderInput('petName', inputData.petName)}
          </div>

          <div className={styles.column}>
            <div className={styles.row}>
              <div className={styles.column}>
                {renderInput('dateOfBirth', inputData.dateOfBirth)}
              </div>

              <div className={styles.column}>
                {renderInput('age', inputData.age)}
              </div>
            </div>

            <div className={styles.row}>
              <span className={styles.dobDisclaimer}>
                {t('addPet.dateOfBirthDisclaimer')}
              </span>
            </div>
          </div>

          <div className={styles.row}>
            <div className={`${styles.column} ${styles.weightContainer}`}>
              {renderInput('weight', inputData.weight)}

              <span className={styles.weightDisclaimer}>
                {t('addPet.weightDisclaimer')}
              </span>
            </div>

            <div className={styles.column}>
              {renderInput('breed', inputData.breed)}
            </div>
          </div>
        </div>

        <div className={styles.buttonContainer}>
          <ActionButton
            className={styles.buttonFullWidthMobile}
            disabled={anyFieldEmpty || invalidDob}
            noMargin
            onClick={handleOnSubmit}
            title={t('addPet.saveInformation')}
          />
        </div>
      </div>
    </ModalContainer>
  );
};

export { AddPetDetailsModal };
